import axios from '../axiosInstance'; // adjust path if needed

const formatDateTime = (isoString) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
};


const applyFilterSortPaginate = (records, params) => {
  const { sort = {}, pagination = {}, filter = {} } = params;
  const { field = 'ID', order = 'ASC' } = sort;
  const { page = 1, perPage = 25 } = pagination;

  // 1. Filter
  let filtered = [...records];
  if (Object.keys(filter).length > 0) {
    filtered = filtered.filter(record =>
      Object.entries(filter).every(([key, value]) =>
        String(record[key] || '').toLowerCase().includes(String(value).toLowerCase())
      )
    );
  }

  // 2. Sort
  filtered.sort((a, b) => {
    const aValue = a[field];
    const bValue = b[field];

    if (aValue > bValue) return order === 'ASC' ? 1 : -1;
    if (aValue < bValue) return order === 'ASC' ? -1 : 1;
    return 0;
  });


  // 3. Pagination
  const start = (page - 1) * perPage;
  const end = start + perPage;
  const paginated = filtered.slice(start, end);

  return {
    data: paginated.map(item => ({
      id: item.ID,
      ...item,
      CreatedDate: formatDateTime(item.CreatedDate),
      ModifiedDate: formatDateTime(item.ModifiedDate),
    })),
    total: filtered.length,
  };


};


const dataProvider = {
  getList: async (resource, params) => {
    switch (resource) {
      case 'recipes': {
        const res = await axios.get(`/recipes`);
        return applyFilterSortPaginate(res.data.recipes, params);
      }
      case 'products': {
        const res = await axios.get(`/products`);
        return applyFilterSortPaginate(res.data.products, params);
      }
      case 'configs': {
        const res = await axios.get(`/configs`);
        return applyFilterSortPaginate(res.data.configs, params);
      }
      case 'packages': {
        const res = await axios.get(`/packages`);
        return applyFilterSortPaginate(res.data.packages, params);
      }
      case 'recyclings': {
        const res = await axios.get(`/recyclings`);
        return applyFilterSortPaginate(res.data.Recyclings, params);
      }
      default:
        throw new Error(`Unknown resource: ${resource}`);
    }
  },


  getOne: async (resource, { id }) => {
    const map = {
      recipes: { endpoint: 'recipe', key: 'recipe' },
      products: { endpoint: 'product', key: 'product' },
      configs: { endpoint: 'config', key: 'config' },
      packages: { endpoint: 'package', key: 'package' },
      recyclings: { endpoint: 'recycling', key: 'Recycling' },
    };

    const { endpoint, key } = map[resource] || {};
    if (!endpoint) throw new Error(`Unknown resource: ${resource}`);

    const res = await axios.post(`/${endpoint}`, {
      [`${resource.slice(0, -1)}ID`]: id,
    });

    const item = res.data[key] || {};

    // Flatten ColorScheme with proper lowercased keys
    const colorScheme = item.ColorScheme || {};
    const flattenedColors = {
      primaryColor: colorScheme.PrimaryColor,
      secondaryColor: colorScheme.SecondaryColor,
      checkMarkColor: colorScheme.CheckMarkColor,
      primaryTextColor: colorScheme.PrimaryTextColor,
      secondaryTextColor: colorScheme.SecondaryTextColor,
    };

    // Start building normalized data
    const baseData = {
      ...item,
      ...flattenedColors,
      backgroundImages: res.data.images || item.Images || [], // prefer API field but fallback to embedded
      createdDate: formatDateTime(item.CreatedDate),
      modifiedDate: formatDateTime(item.ModifiedDate),
    };

    const lowercaseKeys = obj =>
      Object.fromEntries(
        Object.entries(obj).map(([k, v]) => [
          k.charAt(0).toLowerCase() + k.slice(1),
          v,
        ])
      );

    // Normalize key casing if needed
    const normalizedItem = ['packages', 'recyclings', 'configs'].includes(resource)
      ? lowercaseKeys(baseData)
      : baseData;

    return {
      data: {
        id,
        ...normalizedItem,
        ...(res.data.ingredients && { ingredients: res.data.ingredients }),
        ...(res.data.steps && { steps: res.data.steps }),
        ...(res.data.Preparations && { preparations: res.data.Preparations }),
      },
    };
  },



    create: async (resource, { data }) => {
  const endpointMap = {
    recipes: 'create-recipe',
    products: 'create-product',
    configs: 'create-config',
    packages: 'create-package',
    recyclings: 'create-recycling',
  };

  const endpoint = endpointMap[resource];
  if (!endpoint) throw new Error(`Unknown resource: ${resource}`);

  let payload;

  if (resource === 'recipes') {
    // your existing recipe logic here...
    const { ingredients = [], steps = [], images = [], ...mainData } = data;

    const lowercaseKeys = (obj) =>
      Object.fromEntries(
        Object.entries(obj).map(([key, val]) => [
          key.charAt(0).toLowerCase() + key.slice(1),
          val,
        ])
      );

    const addSortOrder = (list) =>
      list.map((item, index) => ({
        ...lowercaseKeys(item),
        sortOrder: index + 1,
      }));

    payload = {
      recipeID: 0,
      recipeData: lowercaseKeys(mainData),
      ingredients: addSortOrder(ingredients),
      steps: addSortOrder(steps),
      images: addSortOrder(images),
    };
  } else if (resource === 'configs') {
    // your existing configs logic here...
    const addSortOrder = (list) =>
      list.map((item, index) => ({
        ...item,
        sortOrder: index + 1,
      }));

    payload = {
      configData: {
        ...data,
        backgroundImages: Array.isArray(data.backgroundImages)
          ? addSortOrder(data.backgroundImages)
          : [],
      },
    };
  } else {
    // For other resources: replace 'recyclings' with 'recyclingIDs' if present
    if ('recyclings' in data) {
      const { recyclings, ...rest } = data;
      payload = {
        ...rest,
        recyclingIDs: recyclings,
      };
    } else {
      payload = data;
    }
  }

  try {
    const res = await axios.post(`/${endpoint}`, payload);

    const id =
      res.data.recipeID ??
      res.data.productID ??
      res.data.configID ??
      res.data.packageID ??
      res.data.recyclingID ??
      res.data.id;

    if (id == null) {
      throw new Error('Missing ID in create response');
    }

    return {
      data: {
        id,
        ...data,
        message: res.data.message,
      },
    };
  } catch (error) {
    const message =
      error.response?.data?.message ||
      error.message ||
      'An unknown error occurred';

    throw new Error(message);
  }
},



    update: async (resource, { id, data }) => {
      console.log("updateCalled");
    const endpointMap = {
      recipes: 'update-recipe',
      products: 'update-product',
      configs: 'update-config',
      packages: 'update-package',
      recyclings: 'update-recycling'
    };

    const lowercaseKeys = (obj) =>
      Object.fromEntries(
        Object.entries(obj).map(([key, val]) => [
          key.charAt(0).toLowerCase() + key.slice(1),
          val
        ])
    );

    const payloadMap = {
      recipes: () => {
        const { ingredients = [], steps = [], images = [], ...mainData } = data;

        const addSortOrder = (list) =>
          list.map((item, index) => ({
            ...lowercaseKeys(item),
            sortOrder: index + 1
          }));

        return {
          recipeID: Number(id),
          recipeData: lowercaseKeys(mainData),
          ingredients: addSortOrder(ingredients),
          steps: addSortOrder(steps),
          images: addSortOrder(images)
        };
      },

      products: () => {
        const productID = Number(id);

        const { Name, Config, Recipes } = data;

        if (!productID || !Name) {
          throw new Error('Missing required fields: ID or Name');
        }

        return {
          productID,
          name: Name,
          configID: Config || 0,
          recipeIDs: Array.isArray(Recipes) ? Recipes : []
        };
      },

      configs: () => {
        const configID = Number(id);
        if (!configID || !data.name) {
          throw new Error('Missing required fields: ID or Name');
        }

        const addSortOrder = (list) =>
          list.map((item, index) => ({
            ...lowercaseKeys(item),
            sortOrder: index + 1,
          }));

        return {
          configID,
          configData: {
            ...data,
            backgroundImages: Array.isArray(data.backgroundImages)
              ? addSortOrder(data.backgroundImages)
              : [],
          },
        };
      },

      packages: () => {
        const packageID = Number(id);
        if (!packageID || !data.name) {
          throw new Error('Missing required fields: ID or Name');
        }

        const recyclingIDs = data.recyclings;

        return {
          ...data,
          packageID,
          recyclingIDs,
        };
      },


      recyclings: () => {
        const recyclingID = Number(id);
        const recyclingData = lowercaseKeys(data);

        if (!recyclingID || !recyclingData.name) {
          throw new Error('Missing required fields: ID or name');
        }

        // Extract preparations and remove from main data
        const { preparations = [], ...rest } = recyclingData;

        return {
          recyclingID,
          ...rest,         // flatten all recycling fields
          preparations     // as a root key
        };
      }
    };

    const endpoint = endpointMap[resource];
    const payloadBuilder = payloadMap[resource];

    if (!endpoint || !payloadBuilder) {
      throw new Error(`Update not implemented for resource: ${resource}`);
    }

    const payload = payloadBuilder();

    await axios.post(`/${endpoint}`, payload);

    return { data: { id, ...data } };
  },


  delete: async (resource, { id }) => {
    const payload = {
      recipes: { recipeID: id },
      products: { productID: id },
      configs: { configID: id },
      packages: { packageID: id },
      recyclings: { recyclingID: id }
    };

    const endpointMap = {
      recipes: 'delete-recipe',
      products: 'delete-product',
      configs: 'delete-config',
      packages: 'delete-package',
      recyclings: 'delete-recycling'
    };

    const endpoint = endpointMap[resource];
    if (!endpoint) throw new Error(`Unknown resource: ${resource}`);

    await axios.post(`/${endpoint}`, payload[resource]);
    return { data: { id } };
  },

  deleteMany: async (resource, { ids }) => {
    const payloadMap = {
      recipes: id => ({ recipeID: id }),
      products: id => ({ productID: id }),
      configs: id => ({ configID: id }),
      packages: id => ({ packageID: id }),
      recyclings: id => ({ recyclingID: id })
    };

    const endpointMap = {
      recipes: 'delete-recipe',
      products: 'delete-product',
      configs: 'delete-config',
      packages: 'delete-package',
      recyclings: 'delete-recycling'
    };

    const endpoint = endpointMap[resource];
    const makePayload = payloadMap[resource];

    if (!endpoint || !makePayload) {
      throw new Error(`Unknown resource: ${resource}`);
    }

    const failed = [];

    for (const id of ids) {
      try {
        await axios.post(`/${endpoint}`, makePayload(id));
      } catch (error) {
        failed.push(id);
      }
    }

    const total = ids.length;
    const failedCount = failed.length;
    const successCount = total - failedCount;

    const message =
      failedCount === 0
        ? 'Todos os itens selecionados foram eliminados com sucesso.'
        : `${successCount} item(ns) eliminado(s) com sucesso. ${failedCount} falhou(falharam).`;

    return {
      data: ids.filter(id => !failed.includes(id)),
      message,
    };
  },


  listFiles: async () => {
    const res = await axios.get('/list-files');
    const data = res.data;

    if (!data.companyID) {
      throw new Error('Missing companyID in list-files response');
    }

    // Return companyID and files directly
    return {
      companyID: data.companyID,
      Images: data.Images || [],
      Pdfs: data.Pdfs || [],
      Videos: data.Videos || [],
    };
  },


  uploadFiles: async (files) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));

    const res = await axios.post(`/upload-files`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return res.data;
  },

  generateRecipePDF: async (recipeID) => {
    const res = await axios.post(`/generate-recipe-pdf`, { recipeID }, {
      responseType: 'blob'
    });

    return res.data;
  },

  checkDuplicates: async (fileNames) => {
    const res = await axios.post(`/check-duplicates`, { fileNames });
    return res.data.duplicates;
  },

  getFileUrl: (filename, companyID) => {
    if (!companyID || !filename) return '';
    return `${axios.defaults.baseURL}/images/${companyID}/${filename}`;
  },


  registerCompany: async (name) => {
    return axios.post(`/register-company`, { name });
  },

  updateCompany: async (name) => {
    return axios.post(`/update-company`, { name });
  },

  openRegisterUser: async ({ name, login, password, permissions, companyID }) => {
    return axios.post(`/open-register-user`, {
      name,
      login,
      password,
      permissions,
      companyID
    });
  },

  registerUser: async ({ name, login, password, permissions }) => {
    return axios.post(`/register-user`, {
      name,
      login,
      password,
      permissions
    });
  },

  updateUserPermissions: async ({ userID, permissions }) => {
    return axios.post(`/update-user`, {
      userID,
      permissions
    });
  },

  deleteUser: async (userID) => {
    return axios.post(`/delete-user`, { userID });
  }
};

export default dataProvider;
